interface LookImage {
  src: string;
  alt: string;
}

interface LookSet {
  main: LookImage;
  supports: LookImage[];
}

const LOOK_SETS: LookSet[] = [
  {
    main: {
      src: 'https://readdy.ai/api/search-image?query=elegant%20Korean%20groom%20in%20premium%20navy%20bespoke%20suit%20full%20body%20portrait%20soft%20natural%20light%20editorial%20wedding%20photography%20minimal%20warm%20cream%20background%20luxury%20menswear%20quiet%20sophistication%20high-end%20magazine%20cover%20quality&width=540&height=700&seq=yb-lb-s1-main&orientation=portrait',
      alt: '빌리언즈테일러 메인 화보',
    },
    supports: [
      {
        src: 'https://readdy.ai/api/search-image?query=premium%20Italian%20navy%20wool%20suit%20fabric%20lapel%20detail%20close-up%20soft%20golden%20light%20buttonhole%20stitching%20luxury%20menswear%20editorial%20product%20photography%20warm%20ambient%20cream%20background&width=360&height=360&seq=yb-lb-s1-d1&orientation=squarish',
        alt: '원단 디테일 컷',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=romantic%20Korean%20wedding%20couple%20holding%20hands%20soft%20warm%20hotel%20interior%20lighting%20editorial%20luxury%20wedding%20photography%20elegant%20intimate%20moment%20cream%20palette&width=360&height=240&seq=yb-lb-s1-d2&orientation=landscape',
        alt: '커플 화보 컷',
      },
    ],
  },
  {
    main: {
      src: 'https://readdy.ai/api/search-image?query=confident%20Korean%20groom%20portrait%20close-up%20navy%20bespoke%20suit%20soft%20window%20light%20editorial%20fashion%20photography%20luxury%20menswear%20sophisticated%20expression%20minimal%20cream%20background&width=540&height=700&seq=yb-lb-s2-main&orientation=portrait',
      alt: '빌리언즈테일러 포트레이트',
    },
    supports: [
      {
        src: 'https://readdy.ai/api/search-image?query=luxury%20Korean%20bespoke%20tailoring%20atelier%20interior%20warm%20ambient%20light%20wooden%20shelves%20fabric%20rolls%20minimalist%20elegant%20space%20soft%20cream%20palette%20premium%20menswear%20shop%20editorial%20wide%20angle&width=360&height=240&seq=yb-lb-s2-d1&orientation=landscape',
        alt: '아틀리에 공간',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=Korean%20groom%20fitting%20session%20adjusting%20jacket%20sleeve%20mirror%20reflection%20soft%20warm%20light%20tailoring%20atelier%20editorial%20wedding%20preparation%20luxury%20menswear%20photography&width=360&height=360&seq=yb-lb-s2-d2&orientation=squarish',
        alt: '피팅 컷',
      },
    ],
  },
];

function LookImageCard({
  src,
  alt,
  className = '',
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl bg-cream-dark ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-105"
        loading="lazy"
      />
    </div>
  );
}

function LookSetLeftMain({ set }: { set: LookSet }) {
  return (
    <div className="grid grid-cols-[1.65fr_1fr] grid-rows-2 gap-3 h-[520px]">
      <LookImageCard
        src={set.main.src}
        alt={set.main.alt}
        className="row-span-2"
      />
      <LookImageCard
        src={set.supports[0].src}
        alt={set.supports[0].alt}
        className="row-span-1"
      />
      <LookImageCard
        src={set.supports[1].src}
        alt={set.supports[1].alt}
        className="row-span-1"
      />
    </div>
  );
}

function LookSetRightMain({ set }: { set: LookSet }) {
  return (
    <div className="grid grid-cols-[1fr_1.65fr] grid-rows-2 gap-3 h-[520px]">
      <LookImageCard
        src={set.supports[0].src}
        alt={set.supports[0].alt}
        className="row-span-1"
      />
      <LookImageCard
        src={set.main.src}
        alt={set.main.alt}
        className="row-span-2"
      />
      <LookImageCard
        src={set.supports[1].src}
        alt={set.supports[1].alt}
        className="row-span-1"
      />
    </div>
  );
}

export default function LookbookSection() {
  return (
    <section className="mt-8 md:mt-10">
      <h3 className="text-xs font-semibold tracking-widest text-softgold uppercase mb-4">
        Brand Lookbook
      </h3>

      {/* Mobile: selective preview (기존 유지) */}
      <div className="md:hidden">
        <div className="aspect-[3/4] rounded-xl overflow-hidden">
          <img
            src={LOOK_SETS[0].main.src}
            alt="대표 화보"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {LOOK_SETS.flatMap((s) => s.supports)
            .slice(0, 4)
            .map((img, i) => (
              <div key={i} className="aspect-square rounded-xl overflow-hidden">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
        </div>
        <button className="mt-4 w-full py-3 border border-cream-dark rounded-xl text-sm text-navy font-medium flex items-center justify-center gap-1 hover:bg-cream transition-colors cursor-pointer whitespace-nowrap">
          전체 화보 48장 보기
          <i className="ri-arrow-right-line" />
        </button>
      </div>

      {/* Desktop: editorial collage */}
      <div className="hidden md:flex flex-col gap-5">
        <LookSetLeftMain set={LOOK_SETS[0]} />
        <LookSetRightMain set={LOOK_SETS[1]} />

        <button className="mt-2 w-full py-3.5 border border-cream-dark rounded-xl text-sm text-navy font-medium flex items-center justify-center gap-1.5 hover:bg-cream transition-colors cursor-pointer whitespace-nowrap">
          전체 화보 48장 보기
          <i className="ri-arrow-right-line" />
        </button>
      </div>
    </section>
  );
}